<template>
  <section
    :id="`ls-sec-${section.key}`"
    class="ls-section"
    :class="`ls-section--${section.kind === 'highlights' ? 'highlights' : 'group'}`"
    :aria-labelledby="`ls-sec-${section.key}-title`"
  >
    <header class="ls-section__head">
      <h2
        :id="`ls-sec-${section.key}-title`"
        class="ls-section__title"
      >{{ section.title }}</h2>
      <span class="ls-section__count">{{ countLabel }}</span>
    </header>
    <p
      v-if="section.intro"
      class="ls-section__intro"
      data-test="ls-section-intro"
    >{{ section.intro }}</p>
    <ul class="ls-section__grid">
      <li
        v-for="product in section.products"
        :key="product._id"
      >
        <div
          v-if="product.pending"
          class="ls-section__pending"
          data-test="ls-card-pending"
          aria-hidden="true"
        >
          <span class="ls-section__pending-media" />
          <span class="ls-section__pending-body" />
        </div>
        <ls-product-card
          v-else
          :product="product"
          :line="productLines[product._id] || ''"
          :price-visibility="priceVisibility"
          :shortlisted="shortlist.includes(product._id)"
          :large="section.kind === 'highlights'"
          :feedback-on="feedbackOn"
          :interested="Boolean(feedbackItems[product._id] && feedbackItems[product._id].interested)"
          :has-comment="Boolean(feedbackItems[product._id] && feedbackItems[product._id].comment.trim())"
          @toggle="id => $emit('toggle', id)"
          @interest="id => $emit('interest', id)"
          @open="id => $emit('open', id)"
        />
      </li>
    </ul>
  </section>
</template>

<script>
import LsProductCard from './LsProductCard.vue'

export default {
  name: 'LsSection',
  components: { LsProductCard },
  props: {
    section: { type: Object, required: true },
    productLines: { type: Object, default: () => ({}) },
    priceVisibility: { type: Object, default: undefined },
    shortlist: { type: Array, default: () => [] },
    // KP-4572: the buyer's feedback by product id, and whether it is available.
    feedbackItems: { type: Object, default: () => ({}) },
    feedbackOn: { type: Boolean, default: false },
  },
  computed: {
    countLabel() {
      const n = this.section.total || this.section.products.length
      return `${n} ${n === 1 ? 'style' : 'styles'}`
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls-section {
  padding: clamp(40px, 7vw, 88px) 0 0;
  scroll-margin-top: 112px;

  @include ls-up($ls-xl) {
    scroll-margin-top: 72px;
  }

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--ls-ink);
  }

  &__title {
    @include ls-heading;
    margin: 0;
    color: var(--ls-ink);
    font-size: clamp(1.75rem, 3.6vw, 2.75rem);
  }

  &__count {
    flex: none;
    @include ls-label;
    color: var(--ls-muted);
    font-weight: 500;
  }

  &__intro {
    margin: 18px 0 0;
    max-width: 62ch;
    color: var(--ls-muted);
    font: 400 1.0625rem/1.6 var(--ls-body-font);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px 12px;
    margin: 28px 0 0;
    padding: 0;
    list-style: none;

    @include ls-up($ls-md) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 44px 20px;
    }

    @include ls-up($ls-xl) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 56px 24px;
    }
  }

  // Highlights read larger: two up on phones, three up from tablets.
  &--highlights &__grid {
    @include ls-up($ls-md) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 48px 24px;
    }

    @include ls-up($ls-lg) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

// A style still loading keeps a card's footprint (4:5 image and a card body),
// so the page holds still when it arrives.
.ls-section__pending {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ls-section__pending-media {
  display: block;
  aspect-ratio: 4 / 5;
  background: var(--ls-surface);
  border-radius: var(--ls-radius);
}

.ls-section__pending-body {
  display: block;
  min-height: 92px;
}
</style>
